'use client';
import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useStore } from '@base-ui/utils/store';
import { CalendarGrid } from '@mui/x-scheduler-internals/calendar-grid';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-internals/use-event-calendar-store-context';
import { useEventOccurrencesWithDayGridPosition } from '@mui/x-scheduler-internals/use-event-occurrences-with-day-grid-position';
import { eventCalendarPreferenceSelectors } from '@mui/x-scheduler-internals/event-calendar-selectors';
import { schedulerOtherSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import {
  formatWeekNumber,
  getEndOfDayMs,
  getWeekNumber,
} from '@mui/x-scheduler-internals/internals';
import type { MonthViewWeekRowProps } from './MonthViewWeekRow.types';
import { MonthViewCell } from './MonthViewCell';
import { useEventCalendarStyledContext } from '../../event-calendar/EventCalendarStyledContext';

const FIXED_CELL_WIDTH = 28;

const MonthViewRow = styled(CalendarGrid.DayRow, {
  name: 'MuiEventCalendar',
  slot: 'MonthViewRow',
})(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))',
  '&[data-show-week-number]': {
    gridTemplateColumns: `${FIXED_CELL_WIDTH}px repeat(auto-fit, minmax(0, 1fr))`,
  },
  '&:not(:last-child)': {
    borderBlockEnd: `1px solid ${(theme.vars || theme).palette.divider}`,
  },
}));

const MonthViewWeekNumberCell = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'MonthViewWeekNumberCell',
})(({ theme }) => ({
  padding: theme.spacing(1, 0),
  textAlign: 'center',
  fontSize: theme.typography.caption.fontSize,
  lineHeight: '18px',
  color: (theme.vars || theme).palette.text.secondary,
}));

export default function MonthViewWeekRow(props: MonthViewWeekRowProps) {
  const { rowIndex, maxEvents, days, occurrencesMap, firstDayRef } = props;

  const store = useEventCalendarStoreContext();
  const showWeekNumber = useStore(store, eventCalendarPreferenceSelectors.showWeekNumber);
  const weekInfo = useStore(store, eventCalendarPreferenceSelectors.weekInfo);
  const locale = useStore(store, schedulerOtherSelectors.dateLocale);
  const { schedulerId, classes, localeText } = useEventCalendarStyledContext();
  const occurrences = useEventOccurrencesWithDayGridPosition({ days, occurrencesMap, maxEvents });
  const weekNumber = getWeekNumber(days[0].plainDate, weekInfo);

  const displayTimezone = useStore(store, schedulerOtherSelectors.displayTimezone);
  // `days[0].timestamp` is the first instant of the first day.
  const start = days[0].timestamp;
  const end = getEndOfDayMs(days[days.length - 1].timestamp, displayTimezone);

  const weekNumberId = showWeekNumber
    ? `${schedulerId}-MonthViewWeekNumber-${weekNumber}`
    : undefined;

  return (
    <MonthViewRow
      className={classes.monthViewRow}
      start={start}
      end={end}
      rowIndex={rowIndex}
      data-show-week-number={showWeekNumber || undefined}
    >
      {showWeekNumber && (
        <MonthViewWeekNumberCell
          className={classes.monthViewWeekNumberCell}
          id={weekNumberId}
          aria-label={localeText.weekNumberAriaLabel(weekNumber)}
          aria-hidden="true"
        >
          {formatWeekNumber(locale, weekNumber)}
        </MonthViewWeekNumberCell>
      )}
      {occurrences.days.map((day, dayIdx) => (
        <MonthViewCell
          ref={dayIdx === 0 ? firstDayRef : undefined}
          key={day.key}
          day={day}
          maxEvents={maxEvents}
          row={occurrences}
          colIndex={dayIdx + 1}
          ariaLabelledBy={weekNumberId}
        />
      ))}
    </MonthViewRow>
  );
}
